import { useQuery } from '@tanstack/react-query';
import { legacy } from '@/shared/api/client';

/**
 * Painel do motor de fidelidade Habib's.
 *
 * Fala com os endpoints `/v1/loyalty-admin/*` da legacy-api (via BFF), que são
 * o lado ADMIN do módulo — nada aqui usa o contrato `/loyalty/*` que o PDV
 * deles consome.
 *
 * Existe por dois motivos: a promoção passou a ser calculada por nós (resposta
 * do Habib's em 01/09), então alguém precisa cadastrar campanha sem abrir o
 * Mongo; e a liquidação é assíncrona, com falha SILENCIOSA — a venda passa
 * normal no caixa e o ponto só não entra 24 horas depois.
 */

/** 1 Balcão, 2 Salão, 3 Delivery, 6 Drive, 13 Auto Atendimento. */
export const SALE_CATEGORIES = [
  { value: 1, label: 'Balcão' },
  { value: 2, label: 'Salão' },
  { value: 3, label: 'Delivery' },
  { value: 6, label: 'Drive' },
  { value: 13, label: 'Auto atendimento' },
] as const;

/** Só os meios que aparecem no fluxo deles; o dicionário completo vai de 1 a 24. */
export const PAYMENT_TYPES = [
  { value: 1, label: 'Dinheiro' },
  { value: 3, label: 'Cartão de crédito' },
  { value: 4, label: 'Pontos de fidelidade' },
  { value: 7, label: 'Outros' },
  { value: 9, label: 'Cartão de débito' },
  { value: 20, label: 'Pede Pronto' },
  { value: 21, label: 'PIX' },
] as const;

export const BENEFIT_KINDS = [
  { value: 'fixed_price', label: 'Preço fixo', hint: 'o item passa a custar este valor' },
  { value: 'amount', label: 'Desconto em R$', hint: 'desconta este valor do item' },
  { value: 'percentage', label: 'Desconto em %', hint: 'desconta esta porcentagem do item' },
] as const;

export type BenefitKind = (typeof BENEFIT_KINDS)[number]['value'];

export interface CampaignBenefit {
  productCode: string;
  kind: BenefitKind;
  value: number;
  maxQuantity: number | null;
}

export interface LoyaltyCampaign {
  _id: string;
  campaignId: number;
  idPassbook: string;
  name: string;
  categoryName: string;
  descriptionCampaign: string;
  descriptionCommercial: string;
  image: string | null;
  order: number;
  /** Grupo de exclusividade. 0 não conflita com ninguém. */
  idConflict: number;
  active: boolean;
  startDate: string;
  endDate: string | null;
  /** Vazio = todos os canais. */
  channels: number[];
  /** Vazio = todos os meios de pagamento. */
  paymentTypes: number[];
  /** Vazio = todas as lojas. */
  storeCodes: string[];
  requiresEnrollment: boolean;
  isExclusive: boolean;
  promocode: string | null;
  applyDiscountOnSale: boolean;
  discountAtItem: boolean;
  cumulative: boolean;
  isGift: boolean;
  usageLimitPerCustomer: number | null;
  benefits: CampaignBenefit[];
}

export type NsuStatus = 'prepared' | 'placed' | 'confirmed' | 'settled' | 'cancelled';

export interface LoyaltyTransaction {
  _id: string;
  nsu: string;
  ssn: string;
  storeCode: string;
  status: NsuStatus;
  /** Venda de cliente bloqueado: responde sucesso e nunca é lançada. */
  shadow: boolean;
  priceTotal: number;
  priceDiscount: number;
  pointsCredited: number;
  pointsDebited: number;
  preparedAt: string;
  confirmedAt: string | null;
  /** confirmedAt + 24h. É por este campo que o job varre. */
  settleAfter: string | null;
  settledAt: string | null;
  settleAttempts: number;
  cancelReason: string | null;
}

export interface SettlementHealth {
  prepared: number;
  placed: number;
  confirmed: number;
  settled: number;
  cancelled: number;
  /** Tem de ser ZERO. Diferente disso, a liquidação parou. */
  overdue: number;
  withFailures: number;
  checkedAt: string;
}

interface Envelope<T> {
  data: T;
}

export function useLoyaltyCampaigns(idPassbook: string | undefined) {
  return useQuery({
    queryKey: ['loyalty-campaigns', idPassbook],
    queryFn: async () => {
      const res = await legacy.get<Envelope<LoyaltyCampaign[]>>(
        `/loyalty-admin/campaigns/${idPassbook}`,
      );
      return Array.isArray(res?.data) ? res.data : [];
    },
    enabled: Boolean(idPassbook),
    staleTime: 60 * 1000,
  });
}

export async function saveLoyaltyCampaign(
  idPassbook: string,
  payload: Partial<LoyaltyCampaign>,
): Promise<LoyaltyCampaign> {
  const res = await legacy.post<Envelope<LoyaltyCampaign>>(
    `/loyalty-admin/campaigns/${idPassbook}`,
    payload,
  );
  return res.data;
}

export async function setCampaignActive(
  idPassbook: string,
  campaignId: number,
  active: boolean,
): Promise<void> {
  await legacy.put(`/loyalty-admin/campaigns/${idPassbook}/${campaignId}/active`, { active });
}

export interface TransactionFilters {
  status?: NsuStatus | '';
  ssn?: string;
  nsu?: string;
  storeCode?: string;
}

export function useLoyaltyTransactions(
  idPassbook: string | undefined,
  filters: TransactionFilters,
) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.ssn) params.set('ssn', filters.ssn);
  if (filters.nsu) params.set('nsu', filters.nsu);
  if (filters.storeCode) params.set('storeCode', filters.storeCode);
  const qs = params.toString();

  return useQuery({
    queryKey: ['loyalty-transactions', idPassbook, qs],
    queryFn: async () => {
      const res = await legacy.get<Envelope<LoyaltyTransaction[]>>(
        `/loyalty-admin/transactions/${idPassbook}${qs ? `?${qs}` : ''}`,
      );
      return Array.isArray(res?.data) ? res.data : [];
    },
    enabled: Boolean(idPassbook),
  });
}

/** Uma campanha que o CPF ativou no app. A janela de 24h vem calculada da API. */
export interface LeadCampaignEnrollment {
  campaignId: number;
  /** null quando a campanha foi apagada do cadastro. */
  name: string | null;
  campaignActive: boolean;
  startDate: string | null;
  endDate: string | null;
  enabledAt: string;
  expiresAt: string;
  live: boolean;
  /** Travada por uma venda em andamento no PDV. */
  locked: boolean;
}

export function useLeadCampaignEnrollments(idPassbook: string, ssn: string) {
  return useQuery({
    queryKey: ['loyalty-lead-enrollments', idPassbook, ssn],
    queryFn: async () => {
      const res = await legacy.get<Envelope<LeadCampaignEnrollment[]>>(
        `/loyalty-admin/enrollments/${idPassbook}?ssn=${encodeURIComponent(ssn)}`,
      );
      return Array.isArray(res?.data) ? res.data : [];
    },
    enabled: Boolean(idPassbook && ssn),
  });
}

export function useSettlementHealth(idPassbook: string | undefined) {
  return useQuery({
    queryKey: ['loyalty-settlement-health', idPassbook],
    queryFn: async () => {
      const res = await legacy.get<Envelope<SettlementHealth>>(
        `/loyalty-admin/settlement-health/${idPassbook}`,
      );
      return res.data;
    },
    enabled: Boolean(idPassbook),
    // A falha da liquidação é silenciosa do lado do caixa. Se alguém deixou
    // esta tela aberta, ela mesma percebe.
    refetchInterval: 60 * 1000,
  });
}
